import * as React from 'react'
import { useCallback, useMemo, useState } from 'react'

import { ProjectAccessType, UserAccountPosition } from '~/client/graph'
import Avatar, {
  AvatarSize,
} from '~/client/src/shared/components/Avatar/Avatar'
import { ListSelector } from '~/client/src/shared/components/ListSelector'
import type {
  IListSelectorGroupConfig,
  IListSelectorGroupHeaderInfo,
  IListSelectorSearchConfig,
  IListSelectorSortConfig,
  SelectionMode,
} from '~/client/src/shared/components/ListSelector/types'
import type { IOptionItem } from '~/client/src/shared/components/OptionsList/types'
import { getAccountTypeTranslate } from '~/client/src/shared/constants/ProjectRoles'
import { getAccountPositionTranslate } from '~/client/src/shared/enums/AccountPosition'
import AvatarType from '~/client/src/shared/enums/AvatarType'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import User from '~/client/src/shared/models/User'
import { compareGroupLabelsUnassignedLast } from '~/client/src/shared/utils/compareGroupsByLabel'
import {
  getAppliedTagIds,
  getUserTagFilterMatcher,
} from '~/client/src/shared/utils/userTagFilter'
import { UNASSIGNED } from '~/client/src/shared/utils/ZoneLevelLocationConstants'

import UserListFilters, { USER_TAG_TYPES } from './UserListFilters'
import {
  ICurrentUserInfo,
  IFilterData,
  IUserMetadata,
} from './useUserListSelectorData'

export interface IUserListSelectorProps {
  selectedUserIds: Set<string>
  onUserToggle?: (userId: string) => void
  onToggleAll?: (userIds: string[], selectAll: boolean) => void
  users: User[]
  userMetadata?: Map<string, IUserMetadata>
  currentUser?: ICurrentUserInfo
  filterData?: IFilterData
  title?: string
  showActions?: boolean
  selectionMode?: SelectionMode
  onClose?: () => void
  onApply?: (selectedUserIds: Set<string>) => void
  className?: string
  defaultGrouping?: GroupingOption
  autoFocusSearch?: boolean
}

export enum GroupingOption {
  NONE = 'None',
  COMPANY = 'Company',
  ROLE = 'Role',
  TEAM = 'Team',
  TRADE = 'Trade',
  ACCOUNT_POSITION = 'AccountPosition',
  ACCOUNT_TYPE = 'AccountType',
}

const SEARCH_KEYS: (keyof User)[] = [
  'fullName',
  'email',
  'firstName',
  'lastName',
]

function getSortingOptions(): IOptionItem[] {
  return [{ key: 'name', label: Localization.translator.name, glyph: 'name' }]
}

function getGroupingOptions(): IOptionItem[] {
  return [
    {
      key: GroupingOption.COMPANY,
      label: Localization.translator.company,
      glyph: 'company',
    },
    { key: GroupingOption.ROLE, label: Localization.translator.role },
    { key: GroupingOption.TEAM, label: Localization.translator.team },
    { key: GroupingOption.TRADE, label: Localization.translator.trade },
    {
      key: GroupingOption.ACCOUNT_POSITION,
      label: Localization.translator.accountPosition,
    },
    {
      key: GroupingOption.ACCOUNT_TYPE,
      label: Localization.translator.accountType,
    },
  ]
}

interface IUserItem {
  id: string
  user: User
}

function getCategoryKeys(
  userId: string,
  option: GroupingOption,
  userMetadata?: Map<string, IUserMetadata>,
): string[] {
  const meta = userMetadata?.get(userId)
  if (!meta) return [UNASSIGNED]

  switch (option) {
    case GroupingOption.COMPANY:
      return [meta.companyName || UNASSIGNED]
    case GroupingOption.ROLE:
      return meta.roleNames?.length ? meta.roleNames : [UNASSIGNED]
    case GroupingOption.TEAM:
      return meta.teamNames?.length ? meta.teamNames : [UNASSIGNED]
    case GroupingOption.TRADE:
      return meta.tradeNames?.length ? meta.tradeNames : [UNASSIGNED]
    case GroupingOption.ACCOUNT_POSITION:
      return meta.accountPosition
        ? [
            getAccountPositionTranslate(
              meta.accountPosition as UserAccountPosition,
            ),
          ]
        : [UNASSIGNED]
    case GroupingOption.ACCOUNT_TYPE:
      return meta.accountType
        ? [getAccountTypeTranslate(meta.accountType as ProjectAccessType)]
        : [UNASSIGNED]
    default:
      return [GroupingOption.NONE]
  }
}

const UserListSelector: React.FC<IUserListSelectorProps> = ({
  selectedUserIds,
  onUserToggle,
  onToggleAll,
  users,
  userMetadata,
  currentUser,
  filterData,
  title,
  showActions,
  selectionMode,
  onClose,
  onApply,
  className,
  defaultGrouping = GroupingOption.NONE,
  autoFocusSearch,
}) => {
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([])
  const [isOnlyMe, setIsOnlyMe] = useState(false)
  const [isOnlyMyCompany, setIsOnlyMyCompany] = useState(false)

  const appliedTagIds = useMemo(
    () =>
      filterData
        ? getAppliedTagIds(
            selectedTagIds,
            USER_TAG_TYPES,
            filterData.tagsByType,
          )
        : [],
    [selectedTagIds, filterData],
  )

  // Domain-filter users, then wrap as IUserItem
  const items: IUserItem[] = useMemo(() => {
    let result = users

    if (appliedTagIds.length && filterData) {
      const userIdsByTagId = filterData.getUserIdsByTagId()
      const matches = getUserTagFilterMatcher(
        appliedTagIds,
        USER_TAG_TYPES,
        filterData.tagsByType,
        tagId => userIdsByTagId.get(tagId) || [],
      )
      result = result.filter(u => matches(u.id))
    }

    if (isOnlyMe && currentUser) {
      result = result.filter(u => u.id === currentUser.id)
    }

    if (isOnlyMyCompany && currentUser?.companyId && userMetadata) {
      result = result.filter(
        u => userMetadata.get(u.id)?.companyId === currentUser.companyId,
      )
    }

    return result.map(user => ({ id: user.id, user }))
  }, [
    users,
    appliedTagIds,
    filterData,
    isOnlyMe,
    isOnlyMyCompany,
    currentUser,
    userMetadata,
  ])

  const searchConfig = useMemo(
    (): IListSelectorSearchConfig<IUserItem> => ({
      searchFn: (item, query) =>
        SEARCH_KEYS.some(key => {
          const val = item.user[key]
          return typeof val === 'string' && val.toLowerCase().includes(query)
        }),
      autoFocus: autoFocusSearch,
    }),
    [autoFocusSearch],
  )

  const sortConfig = useMemo(
    (): IListSelectorSortConfig<IUserItem> => ({
      options: getSortingOptions(),
      defaultKey: 'name',
      sortFn: (a, b, sortOrder) => {
        const aName = (a.user.lastName || a.user.fullName || '').toString()
        const bName = (b.user.lastName || b.user.fullName || '').toString()
        return aName.localeCompare(bName) * sortOrder
      },
    }),
    [],
  )

  const groupingConfig = useMemo(
    (): IListSelectorGroupConfig<IUserItem> => ({
      options: getGroupingOptions(),
      defaultKey:
        defaultGrouping === GroupingOption.NONE ? null : defaultGrouping,
      buildGroupConfig: (groupKey: string) => {
        const option = groupKey as GroupingOption
        if (option === GroupingOption.NONE) return undefined

        return {
          groupBy: (item: IUserItem) =>
            getCategoryKeys(item.user.id, option, userMetadata),
          // Label only — the band header renders the count in its own slot.
          getGroupLabel: (key: string) => key,
          sortGroups: compareGroupLabelsUnassignedLast,
        }
      },
    }),
    [defaultGrouping, userMetadata],
  )

  const companyAvatarByName = useMemo(() => {
    if (!userMetadata) return new Map<string, string | undefined>()
    const map = new Map<string, string | undefined>()
    for (const meta of userMetadata.values()) {
      if (meta.companyName && !map.has(meta.companyName)) {
        map.set(meta.companyName, meta.companyAvatarUrl || undefined)
      }
    }
    return map
  }, [userMetadata])

  const handleApplyTagFilters = useCallback((tagIds: string[]) => {
    setSelectedTagIds(tagIds || [])
  }, [])

  // A host may leave the current user out of the list (chat group create).
  const canFilterMe = !!currentUser && users.some(u => u.id === currentUser.id)
  const canFilterMyCompany = !!currentUser?.companyId

  const toggleOnlyMe = useCallback(() => setIsOnlyMe(prev => !prev), [])
  const toggleOnlyMyCompany = useCallback(
    () => setIsOnlyMyCompany(prev => !prev),
    [],
  )

  const renderContent = useCallback((item: IUserItem) => {
    const { user } = item

    return (
      <>
        <Avatar
          src={user.avatarUrl}
          size={AvatarSize.Minuscule}
          avatarType={AvatarType.User}
        />
        <span className="tw:truncate">{user.fullName}</span>
      </>
    )
  }, [])

  const renderGroupContent = useCallback(
    (group: IListSelectorGroupHeaderInfo<IUserItem>) => {
      if (companyAvatarByName.has(group.key)) {
        return (
          <Avatar
            src={companyAvatarByName.get(group.key)}
            size={AvatarSize.Minuscule}
            avatarType={AvatarType.Company}
          />
        )
      }
      return null
    },
    [companyAvatarByName],
  )

  const filterSlot = filterData ? (
    <UserListFilters
      tagsByType={filterData.tagsByType}
      tagTypes={USER_TAG_TYPES}
      appliedTagIds={appliedTagIds}
      onApplyFilters={handleApplyTagFilters}
    />
  ) : undefined

  return (
    <ListSelector.Root<IUserItem>
      items={items}
      selectedIds={selectedUserIds}
      onItemToggle={onUserToggle}
      onToggleAll={onToggleAll}
      draft={showActions}
      selectionMode={selectionMode}
      onClose={onClose}
      search={searchConfig}
      sort={sortConfig}
      grouping={groupingConfig}
      className={className}
    >
      {title && <ListSelector.Title title={title} onClose={onClose} />}
      <ListSelector.Toolbar
        quickFilters={
          canFilterMe || canFilterMyCompany
            ? {
                ...(canFilterMe && { isOnlyMe, onToggleMe: toggleOnlyMe }),
                ...(canFilterMyCompany && {
                  isOnlyMyCompany,
                  onToggleMyCompany: toggleOnlyMyCompany,
                }),
              }
            : undefined
        }
        filter={filterSlot}
      />
      <ListSelector.Counter formatTotal={Localization.translator.xUsersCount} />
      <ListSelector.List
        renderItemContent={renderContent}
        renderGroupContent={renderGroupContent}
      />
      {showActions && (
        <ListSelector.Actions onApply={onApply} onClose={onClose} />
      )}
    </ListSelector.Root>
  )
}

export default UserListSelector
